import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
  Chip, CircularProgress, Breadcrumbs, Link, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Checkbox
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Description as DocIcon } from '@mui/icons-material';
import { documentService } from '../services/api';
import { useConfirm } from './Common/ConfirmDialog';

const PHASES = ['PreHire', 'Onboarding', 'Employment', 'Offboarding', 'Other'];

const DocumentTypeManagement = () => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    code: '', name: '', description: '', phase: 'Employment',
    required: false, hasExpiry: false, sortOrder: 0, isActive: true
  });

  useEffect(() => { loadItems(); }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await documentService.getDocumentTypes(true);
      setItems(data);
      setError('');
    } catch (err) {
      setError('Error loading document types: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({ code: '', name: '', description: '', phase: 'Employment',
      required: false, hasExpiry: false, sortOrder: items.length + 1, isActive: true });
    setOpenDialog(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      code: item.code, name: item.name, description: item.description || '',
      phase: item.phase, required: item.required, hasExpiry: item.hasExpiry,
      sortOrder: item.sortOrder, isActive: item.isActive
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ message: 'Сигурни ли сте, че искате да изтриете този тип документ?', danger: true, confirmLabel: 'Delete' }))) return;
    try {
      await documentService.deleteDocumentType(id);
      await loadItems();
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to delete');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await documentService.updateDocumentType(editingItem.id, formData);
      } else {
        await documentService.createDocumentType(formData);
      }
      setOpenDialog(false);
      await loadItems();
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to save');
    }
  };

  const handleChange = (field, value) => setFormData({ ...formData, [field]: value });

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;

  return (
    <Box className="position-management-container">
      <Box className="position-header">
        <Breadcrumbs className="breadcrumbs">
          <Link color="inherit" onClick={() => navigate('/settings')} className="breadcrumb-link">Settings</Link>
          <Typography color="text.primary">Document Types</Typography>
        </Breadcrumbs>
        <Box className="header-content">
          <Box className="title-section">
            <DocIcon className="header-icon" />
            <Box>
              <Typography variant="h4" className="page-title">Document Types</Typography>
              <Typography variant="body1" className="page-subtitle">Manage document types and requirements</Typography>
            </Box>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} className="add-button">
            Add Document Type
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} className="alert">{error}</Alert>}

      <TableContainer component={Paper} className="table-container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Phase</TableCell>
              <TableCell align="center">Required</TableCell>
              <TableCell align="center">Has Expiry</TableCell>
              <TableCell align="center">Documents</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center"><Typography variant="body2" color="text.secondary">No document types found</Typography></TableCell></TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id} hover sx={{ opacity: item.isActive ? 1 : 0.5 }}>
                  <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#1976d2' }}>{item.code}</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">{item.name}</Typography></TableCell>
                  <TableCell><Chip label={item.phase} size="small" variant="outlined" /></TableCell>
                  <TableCell align="center">{item.required && <Chip label="Required" size="small" color="error" />}</TableCell>
                  <TableCell align="center">{item.hasExpiry && <Chip label="Expiry" size="small" color="warning" />}</TableCell>
                  <TableCell align="center"><Chip label={item.documentCount || 0} size="small" variant="outlined" color="primary" /></TableCell>
                  <TableCell align="center"><Chip label={item.isActive ? 'Active' : 'Inactive'} size="small" color={item.isActive ? 'success' : 'default'} /></TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(item)} color="primary"><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(item.id)} color="error"><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Document Type' : 'Add New Document Type'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth label="Code" value={formData.code}
                onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                required margin="normal" inputProps={{ maxLength: 50 }} disabled={!!editingItem} />
              <TextField label="Sort Order" type="number" value={formData.sortOrder}
                onChange={(e) => handleChange('sortOrder', parseInt(e.target.value) || 0)}
                margin="normal" sx={{ width: 120 }} />
            </Box>
            <TextField fullWidth label="Name" value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)} required margin="normal" inputProps={{ maxLength: 200 }} />
            <TextField fullWidth label="Description" value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)} margin="normal" multiline rows={2} inputProps={{ maxLength: 500 }} />
            <FormControl fullWidth margin="normal">
              <InputLabel>Phase</InputLabel>
              <Select value={formData.phase} label="Phase" onChange={(e) => handleChange('phase', e.target.value)}>
                {PHASES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <FormControlLabel control={<Checkbox checked={formData.required} onChange={(e) => handleChange('required', e.target.checked)} />} label="Required" />
              <FormControlLabel control={<Checkbox checked={formData.hasExpiry} onChange={(e) => handleChange('hasExpiry', e.target.checked)} />} label="Has Expiry Date" />
              {editingItem && (
                <FormControlLabel control={<Checkbox checked={formData.isActive} onChange={(e) => handleChange('isActive', e.target.checked)} />} label="Active" />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained">{editingItem ? 'Update' : 'Create'}</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default DocumentTypeManagement;

